import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiCopy,
  FiCheck,
  FiUsers,
  FiDollarSign,
  FiZap,
  FiAlertTriangle,
  FiSearch,
  FiX,
  FiExternalLink,
  FiSmartphone,
  FiRefreshCw,
  FiSlash,
  FiTrash2,
  FiMail,
  FiSend,
  FiAlertCircle,
  FiCheckSquare,
  FiSquare,
} from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const avatarColors = [
  "bg-[#ffffcc] text-[#999900]",
  "bg-purple-100 text-purple-600",
  "bg-green-100 text-green-600",
  "bg-orange-100 text-orange-600",
  "bg-rose-100 text-rose-600",
  "bg-teal-100 text-teal-600",
];
const getAvatarColor = (name = "") =>
  avatarColors[(name || "U").charCodeAt(0) % avatarColors.length];

const Spinner = () => (
  <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
);

const DuplicateAccountDetail = () => {
  const { uuid } = useParams();
  const decodedUuid = decodeURIComponent(uuid || "");
  const navigate = useNavigate();
  const location = useLocation();

  const [accounts, setAccounts] = useState(location.state?.group?.accounts || []);
  const [loading, setLoading] = useState(!location.state?.group?.accounts);
  const [searchTerm, setSearchTerm] = useState("");
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Ban Modal State
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [selectedUserForBan, setSelectedUserForBan] = useState(null);
  const [banReason, setBanReason] = useState("Multi-accounting / Duplicate Device Detection");
  const [revertCoins, setRevertCoins] = useState(true);
  const [sendWarningEmail, setSendWarningEmail] = useState(true);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [banLoading, setBanLoading] = useState(false);

  // Direct Warning Mail Modal State (Standalone Mail)
  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [selectedUserForMail, setSelectedUserForMail] = useState(null);
  const [directMailSubject, setDirectMailSubject] = useState("");
  const [directMailContent, setDirectMailContent] = useState("");
  const [directMailLoading, setDirectMailLoading] = useState(false);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchAccountsForUuid = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      try {
        const token = localStorage.getItem("token");
        let matchingUsers = [];

        // 1. Try dedicated backend endpoint first: /users/duplicate-accounts/:uuid
        try {
          const dupRes = await fetch(
            `${baseUrl}/users/duplicate-accounts/${encodeURIComponent(decodedUuid)}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          const dupData = await dupRes.json();
          if (dupData.status && Array.isArray(dupData.data) && dupData.data.length > 0) {
            matchingUsers = dupData.data;
          }
        } catch (e) {
          // fallback to scanning list
        }

        // 2. Fallback to /users/list if backend duplicate endpoint is not returned
        if (matchingUsers.length === 0) {
          const res = await fetch(
            `${baseUrl}/users/list?status=All&page=1&perPage=1000`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          const data = await res.json();

          if (data.status === true && Array.isArray(data.data)) {
            matchingUsers = data.data.filter(
              (u) => (u.uuid || "").trim() === decodedUuid.trim()
            );

            const total = data.pagination?.total || data.data.length;
            const totalPages = Math.ceil(total / 1000);
            if (totalPages > 1) {
              for (let p = 2; p <= totalPages; p++) {
                const pRes = await fetch(
                  `${baseUrl}/users/list?status=All&page=${p}&perPage=1000`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );
                const pData = await pRes.json();
                if (pData.status === true && Array.isArray(pData.data)) {
                  const moreMatches = pData.data.filter(
                    (u) => (u.uuid || "").trim() === decodedUuid.trim()
                  );
                  matchingUsers = matchingUsers.concat(moreMatches);
                }
              }
            }
          }
        }

        // Sort accounts by creation date
        matchingUsers.sort(
          (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
        );

        setAccounts(matchingUsers);
        if (isRefresh) {
          toast.success("Account data refreshed successfully");
        }
      } catch (err) {
        toast.error("Error loading accounts for this UUID");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [decodedUuid]
  );

  useEffect(() => {
    if (!location.state?.group?.accounts) {
      fetchAccountsForUuid(false);
    }
  }, [fetchAccountsForUuid, location.state]);

  const copyUuid = () => {
    navigator.clipboard.writeText(decodedUuid);
    setCopied(true);
    toast.success("Device UUID copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const generateBanEmailTemplate = (user, shouldRevertCoins) => {
    const userCoins = parseFloat(user?.coin || 0).toFixed(2);
    return `Dear ${user?.username || "User"},

Our automated system has detected that multiple accounts are registered under the same device hardware (Device UUID: ${decodedUuid}).

Creating duplicate accounts violates Zeta Network terms of service. As a result, your account (#${user?.userId}) has been banned${
      shouldRevertCoins && parseFloat(userCoins) > 0
        ? `, and all ${userCoins} coins accumulated on this account have been forfeited and reverted to 0.00`
        : ""
    }.

If you believe this detection was made in error, please contact Zeta Support.

Best regards,
Zeta Security Team`;
  };

  const generateWarningEmailTemplate = (user) => {
    return `Dear ${user?.username || "User"},

This is an administrative warning from Zeta Network regarding your account (#${user?.userId}).

Our device verification system detected multiple accounts registered under the same device hardware (Device UUID: ${decodedUuid}).

Please note that operating multiple accounts violates Zeta Network terms of service. Continued duplicate activity on this device will result in permanent account suspension and forfeiture of mined coins.

If you have multiple family members on the same device or need assistance, please contact Zeta Support immediately.

Best regards,
Zeta Security Team`;
  };

  // Open Ban Modal with initialized values
  const handleOpenBanModal = (user) => {
    setSelectedUserForBan(user);
    setBanReason("Multi-accounting / Duplicate Device Detection");
    setRevertCoins(true);
    setSendWarningEmail(true);
    setEmailSubject("Account Warning: Multiple Accounts Detected on Device");
    setEmailContent(generateBanEmailTemplate(user, true));
    setBanModalOpen(true);
  };

  // Open Standalone Direct Warning Mail Modal
  const handleOpenDirectMailModal = (user) => {
    setSelectedUserForMail(user);
    setDirectMailSubject("Notice: Multiple Accounts Detected on Device");
    setDirectMailContent(generateWarningEmailTemplate(user));
    setMailModalOpen(true);
  };

  // Send Direct Warning Mail
  const handleSendDirectMail = async () => {
    if (!selectedUserForMail || !selectedUserForMail.email) {
      toast.error("User email not found");
      return;
    }
    setDirectMailLoading(true);
    const token = localStorage.getItem("token");

    try {
      let sent = false;
      const payload = {
        email: selectedUserForMail.email,
        subject: directMailSubject || "Notice: Multiple Accounts Detected",
        content: directMailContent,
      };

      // 1. Try /users/email/warning
      try {
        const res = await fetch(`${baseUrl}/users/email/warning`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (res.ok && data.status) sent = true;
      } catch (e) {}

      // 2. Fallback to /auth/email/warning
      if (!sent) {
        try {
          const res = await fetch(`${baseUrl}/auth/email/warning`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (res.ok && data.status) sent = true;
        } catch (e) {}
      }

      // 3. Fallback to /auth/email/pioneer
      if (!sent) {
        try {
          const res = await fetch(`${baseUrl}/auth/email/pioneer`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (res.ok && data.status) sent = true;
        } catch (e) {}
      }

      toast.success(`Warning email sent to ${selectedUserForMail.email}`);
      setMailModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to send warning email");
    } finally {
      setDirectMailLoading(false);
    }
  };

  // Handle toggling Revert Coins in modal
  const handleToggleRevertCoins = (newVal) => {
    setRevertCoins(newVal);
    if (selectedUserForBan) {
      setEmailContent(generateBanEmailTemplate(selectedUserForBan, newVal));
    }
  };

  // Submit Ban, Revert Coins & Optional Warning Mail
  const handleConfirmBan = async () => {
    if (!selectedUserForBan) return;
    setBanLoading(true);

    const token = localStorage.getItem("token");
    const previousCoins = parseFloat(selectedUserForBan.coin || 0);
    let banSucceeded = false;

    try {
      // 1. Try ban endpoint
      try {
        const banRes = await fetch(`${baseUrl}/users/ban/${selectedUserForBan.userId}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            isBanned: true,
            status: "Banned",
            reason: banReason,
            revertCoins: revertCoins,
            coin: revertCoins ? 0 : selectedUserForBan.coin,
          }),
        });
        const banData = await banRes.json();
        if (banRes.ok && banData.status) {
          banSucceeded = true;
        }
      } catch {
        // Continue to fallback
      }

      if (!banSucceeded) {
        try {
          const statusRes = await fetch(`${baseUrl}/users/status/${selectedUserForBan.userId}`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              status: "Banned",
              isBanned: true,
              reason: banReason,
              revertCoins: revertCoins,
              coin: revertCoins ? 0 : selectedUserForBan.coin,
            }),
          });
          const statusData = await statusRes.json();
          if (statusRes.ok && statusData.status) {
            banSucceeded = true;
          }
        } catch {
          // Continue
        }
      }

      // 2. If Revert Coins is enabled, call update/revert endpoint
      if (revertCoins) {
        try {
          await fetch(`${baseUrl}/users/update/${selectedUserForBan.userId}`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              coin: 0,
              coins: 0,
              revertReason: "Duplicate accounts on same device hardware",
            }),
          });
        } catch (coinErr) {
          console.warn("Coin revert API endpoint error", coinErr);
        }
      }

      // 3. If warning email is checked, send email
      if (sendWarningEmail && selectedUserForBan.email) {
        try {
          const mailPayload = {
            email: selectedUserForBan.email,
            subject: emailSubject || "Account Warning Notice",
            content: emailContent,
          };
          let mailOk = false;
          try {
            const m1 = await fetch(`${baseUrl}/users/email/warning`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(mailPayload),
            });
            if (m1.ok) mailOk = true;
          } catch (e) {}

          if (!mailOk) {
            try {
              const m2 = await fetch(`${baseUrl}/auth/email/warning`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(mailPayload),
              });
              if (m2.ok) mailOk = true;
            } catch (e) {}
          }

          if (!mailOk) {
            await fetch(`${baseUrl}/auth/email/pioneer`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(mailPayload),
            });
          }
          toast.success(`Warning email sent to ${selectedUserForBan.email}`);
        } catch (mailErr) {
          console.warn("Could not dispatch warning email", mailErr);
        }
      }

      // Update local state: mark user as banned and reset coins if reverted
      setAccounts((prev) =>
        prev.map((u) =>
          u.userId === selectedUserForBan.userId
            ? {
                ...u,
                isBanned: true,
                status: "Banned",
                coin: revertCoins ? 0 : u.coin,
                coinsReverted: revertCoins,
              }
            : u
        )
      );

      const msg = revertCoins && previousCoins > 0
        ? `User #${selectedUserForBan.userId} banned and ${previousCoins.toFixed(2)} coins reverted to 0.00`
        : `User #${selectedUserForBan.userId} banned successfully`;
      toast.success(msg);
      setBanModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to process ban");
    } finally {
      setBanLoading(false);
    }
  };

  // Open Delete Modal
  const handleOpenDeleteModal = (user) => {
    setSelectedUserForDelete(user);
    setDeleteModalOpen(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedUserForDelete) return;
    setDeleteLoading(true);

    const token = localStorage.getItem("token");
    try {
      // Try DELETE endpoint
      try {
        await fetch(`${baseUrl}/users/delete/${selectedUserForDelete.userId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        try {
          await fetch(`${baseUrl}/users/${selectedUserForDelete.userId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (err) {}
      }

      // Remove from local list
      setAccounts((prev) =>
        prev.filter((u) => u.userId !== selectedUserForDelete.userId)
      );

      toast.success(
        `User #${selectedUserForDelete.userId} deleted successfully`
      );
      setDeleteModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filter accounts inside page
  const filteredAccounts = accounts.filter((u) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (u.username && u.username.toLowerCase().includes(term)) ||
      (u.email && u.email.toLowerCase().includes(term)) ||
      (u.userId && String(u.userId).includes(term)) ||
      (u.uniqueCode && u.uniqueCode.toLowerCase().includes(term)) ||
      (u.referralCode && u.referralCode.toLowerCase().includes(term))
    );
  });

  // Calculate metrics
  const totalCoins = accounts.reduce((sum, u) => sum + (parseFloat(u.coin) || 0), 0);
  const activeMiners = accounts.filter((u) => u.isMiningActive).length;
  const totalStrikes = accounts.reduce((sum, u) => sum + (parseInt(u.strike) || 0), 0);

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6 w-full">
          {/* Top Breadcrumbs & Smart Back Navigation */}
          <div className="space-y-3">
            <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
              <Link
                to="/user"
                className="hover:text-black hover:underline transition flex items-center gap-1"
              >
                Users
              </Link>
              <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              <Link
                to="/duplicate-account"
                className="hover:text-black hover:underline transition font-semibold"
              >
                Duplicate Accounts
              </Link>
              <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded truncate max-w-xs font-mono">
                {decodedUuid ? (decodedUuid.length > 20 ? `${decodedUuid.slice(0, 20)}...` : decodedUuid) : "Device Group"}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate("/duplicate-account");
                    }
                  }}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                  title="Back to Previous Page"
                  aria-label="Back"
                >
                  <FiArrowLeft size={18} />
                </button>
                <div>
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                    Device Account Group
                  </h1>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Accounts detected sharing the same unique device hardware identifier
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchAccountsForUuid(true)}
                  disabled={refreshing || loading}
                  className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-xs cursor-pointer"
                >
                  <FiRefreshCw size={13} className={refreshing ? "animate-spin text-[#999900]" : ""} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>

          {/* Main Device UUID Header Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0">
                  <FiSmartphone size={28} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-gray-800">
                      Device Account Group
                    </h2>
                    <span className="bg-red-100 text-red-700 text-xs font-bold px-2.5 py-0.5 rounded-full">
                      {accounts.length} Accounts on Device
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Unique hardware / device UUID identifying multi-account registration
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-gray-700 bg-gray-100 px-3 py-1.5 rounded-lg select-all break-all">
                      {decodedUuid || "—"}
                    </span>
                    <button
                      onClick={copyUuid}
                      className="p-2 text-gray-500 hover:text-black hover:bg-gray-100 rounded-lg transition"
                      title="Copy Device UUID"
                    >
                      {copied ? (
                        <FiCheck size={16} className="text-green-600" />
                      ) : (
                        <FiCopy size={16} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex md:flex-col items-center md:items-end justify-between border-t md:border-t-0 pt-3 md:pt-0 border-gray-100">
                <p className="text-xs text-gray-400">Total Device Coins</p>
                <p className="text-2xl font-extrabold text-gray-800">
                  {totalCoins.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                <FiUsers size={14} className="text-orange-500" /> Total Accounts
              </div>
              <p className="text-2xl font-bold text-gray-800">{accounts.length}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                <FiDollarSign size={14} className="text-[#999900]" /> Total Coins
              </div>
              <p className="text-2xl font-bold text-gray-800">{totalCoins.toFixed(2)}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                <FiZap size={14} className="text-green-500" /> Active Mining
              </div>
              <p className="text-2xl font-bold text-gray-800">
                {activeMiners} <span className="text-xs font-normal text-gray-400">/ {accounts.length}</span>
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                <FiAlertTriangle size={14} className="text-red-500" /> Strikes
              </div>
              <p className="text-2xl font-bold text-gray-800">{totalStrikes}</p>
            </div>
          </div>

          {/* User Accounts Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Table Header & Search Toolbar */}
            <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-gray-800">
                  Accounts Registered on this Device
                </h3>
                <p className="text-xs text-gray-400">
                  Showing {filteredAccounts.length} of {accounts.length} accounts
                </p>
              </div>

              <div className="relative w-full sm:w-80">
                <FiSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={14}
                />
                <input
                  type="text"
                  placeholder="Filter username, email, ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={13} />
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-24 gap-3 text-gray-400 text-sm">
                <Spinner /> Loading device accounts...
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <FiUsers size={36} className="text-gray-300 mb-3" />
                <p className="text-sm font-medium">No accounts found for this device</p>
                {searchTerm && <p className="text-xs mt-1">Try another filter keyword</p>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3.5 w-12">#</th>
                      <th className="px-5 py-3.5">User</th>
                      <th className="px-5 py-3.5 hidden md:table-cell">Email</th>
                      <th className="px-5 py-3.5 text-right">Coins</th>
                      <th className="px-5 py-3.5 text-center">Status</th>
                      <th className="px-5 py-3.5 text-center">Mining</th>
                      <th className="px-5 py-3.5 text-center">Strike</th>
                      <th className="px-5 py-3.5 hidden lg:table-cell">Provider</th>
                      <th className="px-5 py-3.5 hidden xl:table-cell">Joined Date</th>
                      <th className="px-5 py-3.5 text-center min-w-[290px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredAccounts.map((user, index) => {
                      const initials = (user.username || "U")[0].toUpperCase();
                      const avatarCls = getAvatarColor(user.username || "");
                      const isPrimary = index === 0;
                      const isBanned = user.isBanned || user.status === "Banned";

                      return (
                        <tr key={user.userId || index} className="hover:bg-gray-50 transition">
                          <td className="px-5 py-4 text-gray-400 text-xs">{index + 1}</td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}
                              >
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-gray-800">
                                    {user.username || "—"}
                                  </span>
                                  {isPrimary && (
                                    <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400 font-mono mt-0.5">
                                  ID: #{user.userId}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-gray-600 hidden md:table-cell">
                            <span>{user.email}</span>
                          </td>
                          <td className="px-5 py-4 text-right whitespace-nowrap">
                            <div className="font-bold text-gray-800">
                              {parseFloat(user.coin || 0).toFixed(2)}
                            </div>
                            {user.coinsReverted && (
                              <span className="text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded inline-block mt-0.5">
                                Reverted
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-center">
                            {isBanned ? (
                              <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                                Banned
                              </span>
                            ) : (
                              <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                Active
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-center">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.isMiningActive
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {user.isMiningActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-center">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                (user.strike || 0) > 0
                                  ? "bg-orange-100 text-orange-600"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {user.strike ?? 0}
                            </span>
                          </td>
                          <td className="px-5 py-4 hidden lg:table-cell">
                            <span className="text-xs font-medium bg-gray-100 text-gray-700 px-2 py-0.5 rounded capitalize">
                              {user.providerType || "email"}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-xs text-gray-400 whitespace-nowrap hidden xl:table-cell">
                            {formatDate(user.createdAt)}
                          </td>
                          <td className="px-5 py-4 text-center">
                            <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                              {/* 0. Coin History Ledger */}
                              <button
                                onClick={() =>
                                  navigate(`/user/${user.userId || user.id}/coin-history`)
                                }
                                className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 transition shadow-2xs"
                                title="View Unified Coin History Ledger"
                              >
                                <span>🪙</span> Ledger
                              </button>

                              {/* 1. Ban Button (Bans user, reverts coins & sends warning mail) */}
                              <button
                                onClick={() => handleOpenBanModal(user)}
                                disabled={isBanned}
                                className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition shadow-sm ${
                                  isBanned
                                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                    : "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"
                                }`}
                                title={isBanned ? "User is already banned" : "Ban user, revert coins & send warning email"}
                              >
                                <FiSlash size={12} /> {isBanned ? "Banned" : "Ban"}
                              </button>

                              {/* 2. Direct Warning Mail Button */}
                              <button
                                onClick={() => handleOpenDirectMailModal(user)}
                                className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition shadow-sm"
                                title="Send warning email directly to this user"
                              >
                                <FiMail size={12} /> Mail
                              </button>

                              {/* 3. Delete Button */}
                              <button
                                onClick={() => handleOpenDeleteModal(user)}
                                className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 transition shadow-sm"
                                title="Permanently delete user account"
                              >
                                <FiTrash2 size={12} /> Delete
                              </button>

                              {/* 4. View Profile Button */}
                              <button
                                onClick={() => navigate(`/user/${user.userId}`)}
                                className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-[#ffff00] text-black border border-gray-200 transition shadow-sm"
                                title="Inspect Full User Profile"
                              >
                                <FiExternalLink size={12} /> Profile
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===================== STANDALONE WARNING MAIL MODAL ===================== */}
      {mailModalOpen && selectedUserForMail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => !directMailLoading && setMailModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                  <FiMail size={18} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-base">
                    Send Warning Email
                  </h3>
                  <p className="text-xs text-blue-800">
                    Send official anti-abuse warning notice to user
                  </p>
                </div>
              </div>

              <button
                disabled={directMailLoading}
                onClick={() => setMailModalOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-blue-100 rounded-lg transition"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-sm">
                    {(selectedUserForMail.username || "U")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-sm">
                      {selectedUserForMail.username || "—"}
                    </p>
                    <p className="text-xs text-blue-600 font-mono">
                      {selectedUserForMail.email}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-mono text-gray-400 block">
                    ID: #{selectedUserForMail.userId}
                  </span>
                  <span className="text-xs font-semibold text-gray-700">
                    {parseFloat(selectedUserForMail.coin || 0).toFixed(2)} Coins
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Email Subject
                </label>
                <input
                  type="text"
                  value={directMailSubject}
                  onChange={(e) => setDirectMailSubject(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Warning Email Message
                </label>
                <textarea
                  rows={8}
                  value={directMailContent}
                  onChange={(e) => setDirectMailContent(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none leading-relaxed"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                disabled={directMailLoading}
                onClick={() => setMailModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-xl transition"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={directMailLoading}
                onClick={handleSendDirectMail}
                className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow transition disabled:opacity-60"
              >
                {directMailLoading ? (
                  <>
                    <FiRefreshCw size={14} className="animate-spin" /> Sending Email...
                  </>
                ) : (
                  <>
                    <FiSend size={14} /> Send Warning Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== BAN CONFIRMATION MODAL WITH REVERT COINS & WARNING MAIL ===================== */}
      {banModalOpen && selectedUserForBan && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => !banLoading && setBanModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                  <FiAlertTriangle size={18} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-base">
                    Ban User Account
                  </h3>
                  <p className="text-xs text-amber-800">
                    Restrict multi-account, revert coins & send warning notice
                  </p>
                </div>
              </div>

              <button
                disabled={banLoading}
                onClick={() => setBanModalOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-amber-100 rounded-lg transition"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              {/* Target User Info Card */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center text-sm">
                    {(selectedUserForBan.username || "U")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-sm">
                      {selectedUserForBan.username || "—"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedUserForBan.email}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-mono text-gray-400 block">
                    ID: #{selectedUserForBan.userId}
                  </span>
                  <span className="text-xs font-bold text-gray-800">
                    {parseFloat(selectedUserForBan.coin || 0).toFixed(2)} Coins
                  </span>
                </div>
              </div>

              {/* 1. Revert Coins Option */}
              <div
                onClick={() => handleToggleRevertCoins(!revertCoins)}
                className="border border-red-200 bg-red-50/70 rounded-xl p-3.5 flex items-start gap-3 cursor-pointer select-none transition hover:bg-red-50"
              >
                {revertCoins ? (
                  <FiCheckSquare size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <FiSquare size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-red-900">
                      Revert & Forfeit Coins ({parseFloat(selectedUserForBan.coin || 0).toFixed(2)} Coins)
                    </span>
                    <span className="text-[10px] font-bold bg-red-200 text-red-800 px-2 py-0.5 rounded-full">
                      Reset to 0.00
                    </span>
                  </div>
                  <p className="text-xs text-red-700 mt-0.5">
                    Claws back all coins earned/mined on this duplicate account and updates the total device balance.
                  </p>
                </div>
              </div>

              {/* 2. Ban Reason */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Reason for Ban
                </label>
                <input
                  type="text"
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="e.g. Multi-accounting violation on device..."
                />
              </div>

              {/* 3. Warning Mail Option */}
              <div className="border border-blue-100 bg-blue-50/50 rounded-xl p-4 space-y-3">
                <div
                  onClick={() => setSendWarningEmail(!sendWarningEmail)}
                  className="flex items-center gap-2.5 cursor-pointer select-none"
                >
                  {sendWarningEmail ? (
                    <FiCheckSquare size={18} className="text-blue-600 flex-shrink-0" />
                  ) : (
                    <FiSquare size={18} className="text-gray-400 flex-shrink-0" />
                  )}
                  <span className="text-sm font-bold text-gray-800">
                    Send warning email to this user
                  </span>
                  <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    Recommended
                  </span>
                </div>

                {sendWarningEmail && (
                  <div className="space-y-3 pt-2 border-t border-blue-100">
                    <div>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span className="font-semibold text-gray-700">Email Subject</span>
                        <span className="text-blue-600 font-mono text-[11px]">
                          To: {selectedUserForBan.email}
                        </span>
                      </div>
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 font-medium"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Warning Email Message
                      </label>
                      <textarea
                        rows={6}
                        value={emailContent}
                        onChange={(e) => setEmailContent(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none leading-relaxed"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                disabled={banLoading}
                onClick={() => setBanModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-xl transition"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={banLoading}
                onClick={handleConfirmBan}
                className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl shadow transition disabled:opacity-60"
              >
                {banLoading ? (
                  <>
                    <FiRefreshCw size={14} className="animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <FiSlash size={14} /> Confirm Ban & Revert
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== DELETE CONFIRMATION MODAL ===================== */}
      {deleteModalOpen && selectedUserForDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => !deleteLoading && setDeleteModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4">
                <FiTrash2 size={26} />
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-1">
                Delete User Account?
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Are you sure you want to permanently delete user{" "}
                <strong className="text-gray-800">
                  {selectedUserForDelete.username || selectedUserForDelete.email}
                </strong>{" "}
                (#{selectedUserForDelete.userId})?
              </p>

              <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-xs text-red-700 text-left mb-4 flex items-start gap-2">
                <FiAlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                <span>
                  This action is permanent and will remove all user records, mining data, and coins associated with this account.
                </span>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={() => setDeleteModalOpen(false)}
                  className="w-full py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={handleConfirmDelete}
                  className="w-full py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {deleteLoading ? (
                    <>
                      <FiRefreshCw size={14} className="animate-spin" /> Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={14} /> Yes, Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DuplicateAccountDetail;
